import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import { fragmentDisplayText, fragmentSource } from '../fragmentModel';
import {
  expedienteActionHint,
  expedienteActionLabel,
  expedienteActionView,
  expedienteEntryActionable,
  expedienteStateLabel,
} from '../expedienteModel';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = {
  tab: 'inbox',
  entries: [],
  history: [],
  selectedId: null,
  expandedId: null,
  day: null,
  loading: false,
  loaded: false,
  actingId: null,
  error: null,
  notice: '',
  story: null,
  meta: null,
  fragments: [],
  selected: 0,
};

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function wrap(value, maxChars) {
  const words = String(value || '').split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    if (!word) return;
    if ((line + ' ' + word).trim().length > maxChars) {
      if (line) lines.push(line);
      line = word;
    } else {
      line = `${line} ${word}`.trim();
    }
  });
  if (line) lines.push(line);
  return lines;
}

function entryById(id) {
  return state.entries.find((entry) => entry.entryId === id) || state.entries[0] || null;
}

function selectedEntry() {
  return entryById(state.selectedId);
}

function syncFromSession() {
  const session = useCoreSession.getState();
  state.entries = Array.isArray(session.expediente) ? session.expediente : [];
  state.history = Array.isArray(session.expedienteHistory) ? session.expedienteHistory : [];
  state.day = session.expedienteDay;
  if (!state.selectedId && state.entries.length) state.selectedId = state.entries[0].entryId;
  if (state.selectedId && !state.entries.some((entry) => entry.entryId === state.selectedId)) {
    state.selectedId = state.entries[0]?.entryId || null;
  }
}

async function loadJournal() {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  const session = useCoreSession.getState();
  const [expediente, story, fragments] = await Promise.allSettled([
    session.loadExpediente(),
    session.startStory(),
    session.loadFragments(),
  ]);
  if (expediente.status === 'rejected') {
    state.error = coreErrorMessage(expediente.reason);
  }
  if (story.status === 'fulfilled') {
    const result = story.value || {};
    state.story = result.story || null;
    state.meta = result.state || null;
  } else if (state.error == null) {
    state.error = coreErrorMessage(story.reason);
  }
  if (fragments.status === 'fulfilled') {
    state.fragments = fragments.value || [];
  }
  syncFromSession();
  state.selectedId = state.entries[0]?.entryId || null;
  state.loaded = true;
  state.loading = false;
}

function markRead(entry) {
  if (!entry || entry.state !== 'new') return;
  state.entries = state.entries.map((row) =>
    row.entryId === entry.entryId ? { ...row, state: 'read' } : row,
  );
  useCoreSession
    .getState()
    .readExpedienteEntry(entry.entryId)
    .catch(() => undefined);
}

function selectEntry(entry) {
  if (!entry) return;
  if (state.expandedId !== entry.entryId) state.expandedId = null;
  state.selectedId = entry.entryId;
  state.notice = '';
  markRead(entry);
}

async function runAction(entry) {
  if (!entry || state.actingId) return;
  const target = expedienteActionView(entry);
  if (!target) {
    state.error = 'NO ACTION TARGET';
    return;
  }
  if (entry.state === 'acted') {
    if (target === 'journal') {
      state.tab = 'record';
      state.notice = 'RECORD OPEN';
    } else {
      state.notice = `${expedienteActionLabel(entry)} // OPENING`;
      setTimeout(() => navigateTo(target), 350);
    }
    return;
  }
  if (entry.state === 'expired') return;
  const session = useCoreSession.getState();
  state.actingId = entry.entryId;
  state.error = null;
  state.notice = 'FILING...';
  try {
    const result = await session.actExpedienteEntry(entry.entryId);
    const updated = { ...entry, ...(result?.entry || {}), state: 'acted' };
    state.entries = state.entries.map((row) =>
      row.entryId === entry.entryId ? updated : row,
    );
    syncFromSession();
    state.notice = `${expedienteActionLabel(updated)} // FILED`;
    if (target === 'journal') {
      state.tab = 'record';
    } else {
      setTimeout(() => navigateTo(target), 600);
    }
  } catch (error) {
    state.error = coreErrorMessage(error);
    state.notice = 'ACTION FAILED';
  } finally {
    state.actingId = null;
  }
}

function stateColor(entryState) {
  if (entryState === 'new') return TERMINAL.TEXT_BRIGHT;
  if (entryState === 'acted') return TERMINAL.GREEN;
  if (entryState === 'expired') return TERMINAL.TEXT_DIM;
  return TERMINAL.TEXT;
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // EXPEDIENTE', 8, 5, TERMINAL.TEXT);
  if (width >= 500) {
    const sub = state.day != null ? `INTERCEPTED INBOX // DAY ${state.day}` : 'INTERCEPTED INBOX';
    text(ctx, sub, 232, 5, TERMINAL.TEXT_DIM);
  }
  text(
    ctx,
    session.status === 'online' ? 'RECORD' : 'OFFLINE',
    width - 64,
    5,
    session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM,
  );
}

function drawTabs(ctx, width, y) {
  const tabs = [
    { id: 'inbox', label: 'INBOX' },
    { id: 'record', label: 'RECORD' },
  ];
  const w = width < 500 ? 76 : 92;
  tabs.forEach((tab, index) => {
    const x = 8 + index * (w + 6);
    const active = state.tab === tab.id;
    DopeUI.Button(ctx, x, y, w, 22, tab.label, active ? 'hover' : 'normal');
    interactiveRegistry.register({
      id: `journal-tab-${tab.id}`,
      x,
      y,
      w,
      h: 22,
      onClick: () => {
        state.tab = tab.id;
        state.notice = '';
      },
    });
  });
}

function drawEntryList(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'INTERCEPTS');
  const maxChars = Math.max(10, Math.floor((w - 28) / 8));
  if (!state.entries.length) {
    text(ctx, state.loading ? 'SYNCING...' : state.error || 'NO INTERCEPTS TODAY.', x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    text(ctx, 'THE WIRE IS QUIET.', x + 12, y + 48, TERMINAL.TEXT_DIM);
    return;
  }
  const rowH = w < 240 ? 40 : 52;
  state.entries.forEach((entry, index) => {
    const rowY = y + 28 + index * (rowH + 2);
    const selected = entry.entryId === selectedEntry()?.entryId;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH);
    }
    const title = String(entry.title || 'UNTITLED').slice(0, maxChars);
    const status = `[${expedienteStateLabel(entry.state)}]`;
    text(
      ctx,
      `${selected ? '>' : ' '} ${status} ${title}`,
      x + 10,
      rowY,
      stateColor(entry.state),
    );
    const action = expedienteActionLabel(entry);
    text(
      ctx,
      `${selected ? ' ' : ' '} ${action}`,
      x + 10,
      rowY + 16,
      entry.state === 'expired' ? TERMINAL.TEXT_DIM : TERMINAL.TEXT,
    );
    interactiveRegistry.register({
      id: `journal-entry-${index}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => selectEntry(entry),
    });
  });
}

function drawHistory(ctx, x, y, w, h) {
  if (!state.history.length) return;
  const maxChars = Math.max(10, Math.floor((w - 28) / 8));
  DopeUI.Panel(ctx, x, y, w, h, 'EARLIER');
  state.history.slice(0, 3).forEach((entry, index) => {
    const rowY = y + 28 + index * 18;
    const label = `${expedienteStateLabel(entry.state)} // ${entry.title}`;
    text(ctx, label.slice(0, maxChars), x + 12, rowY, entry.state === 'expired' ? TERMINAL.TEXT_DIM : TERMINAL.TEXT);
  });
}

function drawEntryDetail(ctx, x, y, w, h) {
  const entry = selectedEntry();
  DopeUI.Panel(ctx, x, y, w, h, 'TRANSMISSION');
  if (!entry) {
    text(ctx, state.error || 'NO TRANSMISSION SELECTED.', x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  const maxChars = Math.max(14, Math.floor((w - 28) / 8));
  const muted = entry.state === 'expired';
  text(
    ctx,
    `${expedienteStateLabel(entry.state)} // ${expedienteActionLabel(entry)}`,
    x + 12,
    y + 26,
    stateColor(entry.state),
  );
  text(ctx, String(entry.title || 'UNTITLED'), x + 12, y + 44, muted ? TERMINAL.TEXT_DIM : TERMINAL.TEXT_BRIGHT);

  let cursor = y + 64;
  const bodyLines = wrap(entry.shortBody, maxChars);
  bodyLines.forEach((line) => {
    text(ctx, line, x + 12, cursor, muted ? TERMINAL.TEXT_DIM : TERMINAL.TEXT);
    cursor += 14;
  });

  const hint = expedienteActionHint(entry);
  if (hint && hint !== entry.shortBody) {
    cursor += 4;
    text(ctx, `TIP // ${hint}`.slice(0, maxChars), x + 12, cursor, TERMINAL.GREEN);
    cursor += 14;
  }

  const actionH = 30;
  const actionY = y + h - actionH - 14;
  const expandH = entry.expand ? 22 : 0;
  const expandY = actionY - expandH - 8;
  if (entry.expand) {
    if (state.expandedId === entry.entryId) {
      const availableLines = Math.max(2, Math.floor((expandY - cursor - 8) / 13));
      const lore = wrap(entry.expand, maxChars).slice(0, availableLines);
      let loreY = cursor + 4;
      rule(ctx, x + 12, loreY - 4, w - 24, TERMINAL.GREEN_DARK);
      lore.forEach((line) => {
        text(ctx, line, x + 12, loreY, TERMINAL.TEXT_DIM);
        loreY += 13;
      });
    }
    DopeUI.Button(
      ctx,
      x + 12,
      expandY,
      Math.min(150, w - 24),
      expandH,
      state.expandedId === entry.entryId ? 'COLLAPSE' : 'EXPAND LORE',
      'normal',
    );
    interactiveRegistry.register({
      id: 'journal-expand',
      x: x + 12,
      y: expandY,
      w: Math.min(150, w - 24),
      h: expandH,
      onClick: () => {
        state.expandedId = state.expandedId === entry.entryId ? null : entry.entryId;
        markRead(entry);
      },
    });
  }

  const canAct = expedienteEntryActionable(entry, { loading: state.actingId === entry.entryId });
  DopeUI.Button(
    ctx,
    x + 12,
    actionY,
    w - 24,
    actionH,
    state.actingId === entry.entryId ? 'FILING' : expedienteActionLabel(entry),
    canAct ? 'hover' : 'disabled',
  );
  if (canAct) {
    interactiveRegistry.register({
      id: 'journal-act',
      x: x + 12,
      y: actionY,
      w: w - 24,
      h: actionH,
      onClick: () => runAction(entry),
    });
  }
  rule(ctx, x + 12, actionY - 8, w - 24, TERMINAL.GREEN_DARK);
  const footer = state.error || state.notice || `SOURCE ${String(entry.sourceEventId || 'STREET').slice(-8)}`;
  text(ctx, footer.slice(0, maxChars), x + 12, actionY + actionH + 6, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
}

function drawInbox(ctx, safe) {
  drawTabs(ctx, safe.width, 30);
  const top = 58;
  const footerY = safe.height - 30;
  const mobile = safe.width < 520;
  if (state.loading && !state.entries.length) {
    text(ctx, 'SYNCING THE WIRE...', 12, top + 12, TERMINAL.TEXT_DIM);
    return;
  }
  if (!state.entries.length) {
    const offline = useCoreSession.getState().status !== 'online';
    DopeUI.Panel(ctx, 8, top, safe.width - 16, Math.min(140, footerY - top), 'INTERCEPTS');
    text(ctx, state.error || (offline ? 'OFFLINE // THE WIRE IS DOWN.' : 'NO INTERCEPTS TODAY.'), 20, top + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    text(ctx, offline ? 'RECONNECT TO PULL THE INBOX.' : 'THE WIRE IS QUIET. COME BACK AFTER THE CLOSE.', 20, top + 50, TERMINAL.TEXT_DIM);
    const retryW = 120;
    DopeUI.Button(ctx, 20, top + 70, retryW, 24, 'RE-SYNC', 'normal');
    interactiveRegistry.register({
      id: 'journal-retry',
      x: 20,
      y: top + 70,
      w: retryW,
      h: 24,
      onClick: () => {
        state.loaded = false;
        state.loading = false;
        loadJournal();
      },
    });
    return;
  }
  if (mobile) {
    const listH = Math.min(112, 24 + state.entries.length * 42);
    drawEntryList(ctx, 8, top, safe.width - 16, listH);
    const detailY = top + listH + 6;
    const showHistory = state.history.length > 0 && footerY - detailY > 240;
    const historyH = showHistory ? 70 : 0;
    const detailH = footerY - detailY - (showHistory ? historyH + 6 : 0);
    drawEntryDetail(ctx, 8, detailY, safe.width - 16, Math.max(150, detailH));
    if (showHistory) drawHistory(ctx, 8, footerY - historyH, safe.width - 16, historyH);
  } else {
    const listW = Math.min(230, Math.floor(safe.width * 0.34));
    const historyH = state.history.length ? 92 : 0;
    drawEntryList(ctx, 8, top, listW, footerY - top - (historyH ? historyH + 6 : 0));
    if (historyH) drawHistory(ctx, 8, footerY - historyH, listW, historyH);
    drawEntryDetail(ctx, 8 + listW + 8, top, safe.width - listW - 24, footerY - top);
  }
}

function drawNode(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'STORY // RECORD');
  const node = state.story?.node;
  if (!node) {
    text(ctx, state.error || (state.loading ? 'SYNCING...' : 'NO RECORD.'), x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  text(ctx, `ACT ${state.meta?.act ?? state.story?.act ?? '--'} // ${String(state.meta?.status || 'active').toUpperCase()}`, x + 12, y + 28, TERMINAL.TEXT_DIM);
  text(ctx, String(node.speaker || 'NARRATOR').toUpperCase(), x + 12, y + 48, TERMINAL.GREEN);
  const maxChars = Math.max(16, Math.floor((w - 28) / 8));
  const lines = wrap(node.text, maxChars).slice(0, Math.floor((h - 82) / 16));
  lines.forEach((line, index) => text(ctx, line, x + 12, y + 70 + index * 16, TERMINAL.TEXT));
  rule(ctx, x + 12, y + h - 24, w - 24);
  const footer = state.error || state.notice || `NODE ${node.nodeId}`;
  text(ctx, footer.slice(0, maxChars), x + 12, y + h - 18, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
}

function drawFragments(ctx, x, y, w, h, twoLine = false) {
  const decryptedCount = state.fragments.filter((fragment) => fragment.visibility === 'DECRYPTED').length;
  DopeUI.Panel(ctx, x, y, w, h, `CANON // EXPEDIENTE ${decryptedCount}/${state.fragments.length || 4}`);
  const rowH = (h - 34) / Math.max(1, Math.min(4, state.fragments.length || 4));
  const maxChars = Math.max(18, Math.floor((w - 24) / 8));
  const clip = (value, limit) => String(value || '').slice(0, limit);
  state.fragments.forEach((fragment, index) => {
    const by = y + 28 + index * rowH;
    const decrypted = fragment.visibility === 'DECRYPTED';
    const title = clip(fragment.title || 'UNTITLED', maxChars - 18);
    const source = clip(fragmentSource(fragment), Math.max(8, Math.floor(maxChars / 2) - 2));
    const body = clip(fragmentDisplayText(fragment), Math.max(8, maxChars - source.length - 5));
    if (twoLine) {
      text(ctx, `${index + 1} ${fragment.visibility} // ${title}`, x + 12, by, decrypted ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
      text(ctx, `${source} // ${body}`, x + 12, by + 16, decrypted ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
    } else {
      text(ctx, clip(`${index + 1} ${fragment.visibility} // ${title} // ${body}`, maxChars), x + 12, by, decrypted ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    }
  });
  if (!state.fragments.length) text(ctx, 'NO EXPEDIENTE // ACQUIRE HRONIR', x + 12, y + 30, TERMINAL.TEXT_DIM);
}

function drawChoices(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'CHOICES');
  const choices = state.story?.choices || [];
  if (!choices.length) {
    text(ctx, 'NO CHOICES LEFT.', x + 12, y + 30, TERMINAL.TEXT_DIM);
    text(ctx, 'THE RECORD IS CLOSED.', x + 12, y + 48, TERMINAL.GREEN);
    return;
  }
  const maxChars = Math.max(12, Math.floor((w - 32) / 8));
  const choiceH = 40;
  choices.forEach((choice, index) => {
    const rowY = y + 30 + index * (choiceH + 2);
    const selected = index === state.selected;
    const available = choice.available !== false;
    if (selected) {
      ctx.fillStyle = available ? TERMINAL.GREEN_DARK : TERMINAL.BLACK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, choiceH);
    }
    const lines = wrap(choice.label, maxChars).slice(0, 2);
    lines.forEach((line, lineIndex) => {
      const color = !available
        ? TERMINAL.TEXT_DIM
        : selected
          ? TERMINAL.TEXT_BRIGHT
          : TERMINAL.TEXT;
      text(ctx, `${lineIndex === 0 ? (selected ? '>' : ' ') : ' '} ${line}`, x + 12, rowY + lineIndex * 15, color);
    });
    if (available) {
      interactiveRegistry.register({
        id: `journal-choice-${index}`,
        x: x + 6,
        y: rowY - 4,
        w: w - 12,
        h: choiceH,
        onClick: () => { state.selected = index; state.notice = ''; },
      });
    }
  });

  const canChoose = !state.loading
    && choices.length > 0
    && choices[state.selected]?.available !== false;
  const actionH = w > 300 ? 36 : 24;
  const actionY = y + h - actionH - 12;
  DopeUI.Button(ctx, x + 12, actionY, w - 24, actionH, state.loading ? 'SENDING' : 'CHOOSE', canChoose ? 'hover' : 'disabled');
  if (canChoose) {
    interactiveRegistry.register({
      id: 'journal-choose',
      x: x + 12,
      y: actionY,
      w: w - 24,
      h: actionH,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          const result = await useCoreSession.getState().chooseStory(state.selected);
          state.story = result.story || state.story;
          state.meta = result.state || state.meta;
          state.fragments = await useCoreSession.getState().loadFragments().catch(() => state.fragments);
          state.notice = result.pendingEffects?.length
            ? `${result.pendingEffects.length} EFFECT(S) PENDING`
            : 'RECORD ADVANCED';
          state.selected = 0;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

function drawRecord(ctx, safe) {
  drawTabs(ctx, safe.width, 30);
  const mobile = safe.width < 500;
  if (mobile) {
    drawNode(ctx, 8, 58, safe.width - 16, 130);
    drawFragments(ctx, 8, 194, safe.width - 16, 150, true);
    drawChoices(ctx, 8, 350, safe.width - 16, 160);
  } else {
    drawNode(ctx, 8, 58, 376, 132);
    drawFragments(ctx, 8, 196, 376, 104);
    drawChoices(ctx, 400, 58, 232, 242);
  }
}

function drawFooter(ctx, safe) {
  const footerY = safe.height - 30;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, state.notice || 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, footerY + 8, TERMINAL.TEXT_DIM);
  const backW = safe.width < 500 ? 120 : 150;
  const backX = safe.width - backW - 8;
  const backY = footerY + 3;
  const backH = 22;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'journal-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('main_menu'),
  });
}

registerView('journal', function renderJournal(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const status = useCoreSession.getState().status;
  if (!state.loaded && !state.loading && status === 'online') loadJournal();

  drawTop(ctx, safe.width);
  if (state.tab === 'record') {
    drawRecord(ctx, safe);
  } else {
    drawInbox(ctx, safe);
  }
  drawFooter(ctx, safe);

  if (!(safe.width < 500) && state.tab === 'record') {
    text(ctx, 'EXPEDIENTE // THE RECORD REMEMBERS.', 8, safe.height - 38, TERMINAL.GREEN);
  }
});
